import { useCallback, useContext, useMemo, useState } from "react";
import { uniqBy } from "lodash";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import {
  AccessControlEntityType,
  AccessControlPolicy,
  GetWorkflowQuery,
  useInsertWorkflowStepMutation,
  useWorkflowStepCollaboratorsQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn } from "../Common/__styles__/Layout";
import { InputRow } from "../Common/__styles__/Modal";
import { CommonModal, useModalNavigation } from "../Common/CommonModal";
import Modal from "../Common/Modal";
import {
  Collaborator,
  InheritedPermissionGroup,
} from "../Common/ShareForm/types";
import Select from "../Inputs/Select";
import { ReactHookFormTextInput } from "../Inputs/Text";
import {
  InheritedPermissionsDetail,
  mapGroupPermissionsFromStep,
  WorkflowStepShareForm,
} from "./ShareWorkflowStep";

type FormData = {
  workflowStepDefinitionId: string;
  addAtOrderIndex: number;
  name: string;
  workflowId: string;
  clonedFromStepId: string;
  collaborators: Collaborator[];
};

type InsertWorkflowStepModalProps = {
  addAtOrderIndex: number;
  startingName: string;
  workflowStepDefinition: NonNullable<
    GetWorkflowQuery["workflow"]
  >["workflowSteps"][number]["workflowStepDefinition"];
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
  clonedFromStepId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

type ModalView = "name" | "permissions" | "detail";

export const InsertWorkflowStepModal = ({
  addAtOrderIndex,
  startingName,
  workflowStepDefinition,
  workflow,
  clonedFromStepId,
  onSuccess,
  onCancel,
}: InsertWorkflowStepModalProps) => {
  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const { user, admin } = useContext(AuthContext);
  const { view, goTo, goBack, canGoBack } =
    useModalNavigation<ModalView>("name");
  const [selectedInheritedGroup, setSelectedInheritedGroup] =
    useState<InheritedPermissionGroup | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    setValue,
    watch,
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      name: startingName,
      workflowStepDefinitionId: workflowStepDefinition.id,
      addAtOrderIndex,
      workflowId: workflow.id,
      clonedFromStepId,
      collaborators: [],
    },
  });

  const collaborators = watch("collaborators");

  const { data: stepData, loading: isLoadingCollaborators } =
    useWorkflowStepCollaboratorsQuery({
      variables: { workflowStepId: clonedFromStepId },
      fetchPolicy: "cache-and-network",
    });

  const inheritedPermissions = useMemo(
    () =>
      mapGroupPermissionsFromStep(stepData?.workflowStep?.collaborators ?? []),
    [stepData?.workflowStep?.collaborators],
  );

  const [insertWorkflowStepMutation, { loading }] =
    useInsertWorkflowStepMutation({
      onCompleted: () => {
        addSuccessToast("Added workflow step successfully");
        onSuccess();
        track("Workflow step inserted", {
          workflowId: workflow.id,
          workflowDefinitionId: workflow.workflowDefinition.id,
          workflowDefinitionName: workflow.workflowDefinition.name,
          userId: user?.id ?? admin?.id,
          userEmail: user?.email ?? admin?.email,
        });
      },
      onError: () => {
        addErrorToast(
          `There was an issue adding your workflow step. Please try again or contact support@withforerunner.com`,
        );
      },
    });

  const handleStateChange = useCallback(
    (newCollaborators: Collaborator[]) => {
      setValue("collaborators", newCollaborators);
    },
    [setValue],
  );

  const handleInheritedPermissionGroupClick = useCallback(
    (group: InheritedPermissionGroup) => {
      setSelectedInheritedGroup(group);
      goTo("detail");
    },
    [goTo],
  );

  const onSubmit = async () => {
    const formData = getValues();
    await insertWorkflowStepMutation({
      variables: {
        data: {
          name: formData.name,
          workflowId: formData.workflowId,
          workflowStepDefinitionId: formData.workflowStepDefinitionId,
          addAtOrderIndex: formData.addAtOrderIndex,
          clonedFromStepId: formData.clonedFromStepId,
          collaborators: formData.collaborators.map(c => ({
            entityId: c.id,
            entityType: AccessControlEntityType.PUBLIC_USER,
            policy: c.permission as AccessControlPolicy,
          })),
        },
      },
    });
  };

  const submissionTypeOptions = uniqBy(
    workflow.workflowSteps,
    step => step.workflowStepDefinition.submissionType.id,
  ).map(step => ({
    label: step.workflowStepDefinition.submissionType.name,
    value: step.workflowStepDefinition.submissionType.id,
  }));

  const titles: Record<ModalView, string> = {
    name: "Add step",
    permissions: "Share step",
    detail: selectedInheritedGroup?.label ?? "Group permissions",
  };

  const renderContent = () => {
    switch (view) {
      case "name":
        return (
          <FlexColumn gap="m">
            <InputRow>
              <ReactHookFormTextInput
                label="Name"
                {...register("name", { required: "Name is required" })}
                required
                error={errors.name?.message}
                data-testid="workflow-step-name-input"
              />
            </InputRow>
            <InputRow>
              <Select
                label="Submission type"
                name="submissionTypeId"
                options={submissionTypeOptions}
                value={workflowStepDefinition.submissionType.id}
                onChange={() => {}}
                disabled={true}
                required
              />
            </InputRow>
          </FlexColumn>
        );
      case "permissions":
        return (
          <WorkflowStepShareForm
            collaborators={collaborators}
            inheritedPermissions={inheritedPermissions}
            onStateChange={handleStateChange}
            onInheritedPermissionGroupClick={
              handleInheritedPermissionGroupClick
            }
            isLoading={isLoadingCollaborators}
          />
        );
      case "detail":
        return selectedInheritedGroup ? (
          <InheritedPermissionsDetail group={selectedInheritedGroup} />
        ) : null;
    }
  };

  const getButtonConfig = () => {
    switch (view) {
      case "name":
        return {
          showNextButton: true,
          handleNext: () => goTo("permissions"),
          disabled: !isValid,
        };
      case "permissions":
        return {
          onSubmit: handleSubmit(onSubmit),
          primaryButtonText: "Save",
        };
      case "detail":
        return {};
    }
  };

  return (
    <CommonModal
      title={titles[view]}
      subtitle={workflowStepDefinition.name}
      content={renderContent()}
      onCancel={onCancel}
      loading={loading}
      size="large"
      dataTestId="add-workflow-step-modal"
      overflows={view !== "name"}
      handlePrevious={canGoBack ? goBack : undefined}
      {...getButtonConfig()}
    />
  );
};

export const useInsertWorkflowStepModal = ({
  startingName,
  workflowStepDefinition,
  workflow,
  addAtOrderIndex,
  clonedFromStepId,
}: {
  startingName: string;
  workflowStepDefinition: NonNullable<
    GetWorkflowQuery["workflow"]
  >["workflowSteps"][number]["workflowStepDefinition"];
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
  addAtOrderIndex: number;
  clonedFromStepId: string;
}) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <InsertWorkflowStepModal
          addAtOrderIndex={addAtOrderIndex}
          startingName={startingName}
          workflowStepDefinition={workflowStepDefinition}
          workflow={workflow}
          clonedFromStepId={clonedFromStepId}
          onCancel={hideModal}
          onSuccess={hideModal}
        />
      </Modal>
    ),
    [
      startingName,
      workflowStepDefinition,
      workflow,
      addAtOrderIndex,
      clonedFromStepId,
    ],
  );

  return [showModal, hideModal] as const;
};
